import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Headphones, Link2, Target, Layers, ClipboardCheck, Radio } from 'lucide-react';
import SpeakingPractice from '@/components/SpeakingPractice';
import ListeningChain from '@/components/ListeningChain';
import HtmlModuleFrame from '@/components/HtmlModuleFrame';
import OperationListen from '@/components/OperationListen';
import { VI_DRILLS, FLASHCARDS, AAA_CHECKLIST } from '@/config/drillsCatalog';
import { markDrillSeen, getSeenMap } from '@/lib/drillProgress';

/**
 * Voice Integration hub.
 *  - Tonal Influence: 62-chapter audiobook.
 *  - The Listening Chain: interactive five-gate framework.
 *  - Voice drills: short standalone modules (5 total).
 *  - Flashcards & Daily Nudge: pulls concepts from every drill.
 */
const VOICE_MODULE = {
  HUB: 'hub',
  TONAL: 'tonal',
  CHAIN: 'chain',
  OLG: 'olg',
};

const VoiceIntegration = () => {
  const navigate = useNavigate();
  const [module, setModule] = useState(VOICE_MODULE.HUB);
  const [activeDrill, setActiveDrill] = useState(null);
  const [seenMap, setSeenMap] = useState(() => getSeenMap());

  if (activeDrill) {
    return (
      <HtmlModuleFrame
        src={`/modules/${activeDrill.file}`}
        title={activeDrill.title}
        onExit={() => {
          setSeenMap(getSeenMap());
          setActiveDrill(null);
        }}
        testId={`vi-drill-${activeDrill.id}`}
      />
    );
  }

  if (module === VOICE_MODULE.TONAL) {
    return (
      <SpeakingPractice
        onBack={() => setModule(VOICE_MODULE.HUB)}
        onComplete={() => setModule(VOICE_MODULE.HUB)}
      />
    );
  }

  if (module === VOICE_MODULE.CHAIN) {
    return <ListeningChain onExit={() => setModule(VOICE_MODULE.HUB)} />;
  }

  if (module === VOICE_MODULE.OLG) {
    return <OperationListen onExit={() => setModule(VOICE_MODULE.HUB)} />;
  }

  return (
    <div
      className="min-h-screen bg-[#1A1814] text-[#F5F2EB] flex flex-col overflow-y-auto"
      data-testid="voice-integration-hub"
    >
      <button
        onClick={() => navigate('/client')}
        className="absolute top-safe-6 left-6 flex items-center gap-2 text-[#F5F2EB]/65 hover:text-[#F5F2EB]/80 transition-colors z-10"
        data-testid="voice-hub-back"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">Back</span>
      </button>

      <div className="flex-1 flex items-start justify-center p-6 py-12 sm:py-16">
        <div className="max-w-[860px] w-full">
          <div className="text-center mb-10">
            <div className="text-[11px] font-medium tracking-[0.25em] uppercase text-[#C45A3B] mb-3">
              The Voice
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl leading-[1.05] mb-4 text-[#F5F2EB]">
              Four ways to
              <br />
              <em className="italic text-[#C45A3B]">work your voice.</em>
            </h1>
            <p className="text-[#F5F2EB]/60 text-sm max-w-[460px] mx-auto leading-relaxed">
              A daily audio chapter, a framework for hard conversations, a pre-flight
              checklist, plus short drills for the small dials that change how you land.
            </p>
          </div>

          {/* Primary modules */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <button
              onClick={() => setModule(VOICE_MODULE.TONAL)}
              className="group text-left bg-[#252018] rounded-2xl p-6 border border-[#F5F2EB]/[0.07] hover:border-[#C45A3B]/60 hover:bg-[#2C2620] transition-all"
              data-testid="voice-module-tonal"
            >
              <div className="w-11 h-11 rounded-xl bg-[#C45A3B]/15 border border-[#C45A3B]/30 flex items-center justify-center mb-4">
                <Headphones className="w-5 h-5 text-[#C45A3B]" />
              </div>
              <div className="font-serif text-2xl text-[#F5F2EB] mb-1">Tonal Influence</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#F5F2EB]/50 mb-3">Audiobook · 62 chapters</div>
              <div className="text-sm text-[#F5F2EB]/60 leading-relaxed">
                One chapter a day. Read, listen, and land the day's carry — unlocked twenty-four hours after the last.
              </div>
              <div className="mt-4 inline-flex items-center gap-1 text-[#C45A3B] text-sm font-medium">
                Enter <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </button>

            <button
              onClick={() => setModule(VOICE_MODULE.CHAIN)}
              className="group text-left bg-[#252018] rounded-2xl p-6 border border-[#F5F2EB]/[0.07] hover:border-[#C45A3B]/60 hover:bg-[#2C2620] transition-all"
              data-testid="voice-module-chain"
            >
              <div className="w-11 h-11 rounded-xl bg-[#C45A3B]/15 border border-[#C45A3B]/30 flex items-center justify-center mb-4">
                <Link2 className="w-5 h-5 text-[#C45A3B]" />
              </div>
              <div className="font-serif text-2xl text-[#F5F2EB] mb-1">The Listening Chain</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#F5F2EB]/50 mb-3">Framework · 6 pages</div>
              <div className="text-sm text-[#F5F2EB]/60 leading-relaxed">
                Five gates — Voice, Time, Story, Respect, Common Ground — with a pre-gate posture and eight redirects.
              </div>
              <div className="mt-4 inline-flex items-center gap-1 text-[#C45A3B] text-sm font-medium">
                Enter <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </button>

            <button
              onClick={() => {
                markDrillSeen(`vi-${AAA_CHECKLIST.id}`);
                setSeenMap(getSeenMap());
                setActiveDrill(AAA_CHECKLIST);
              }}
              className="group relative text-left bg-[#252018] rounded-2xl p-6 border border-[#F5F2EB]/[0.07] hover:border-[#C45A3B]/60 hover:bg-[#2C2620] transition-all"
              data-testid="voice-module-aaa"
            >
              <div className="w-11 h-11 rounded-xl bg-[#C45A3B]/15 border border-[#C45A3B]/30 flex items-center justify-center mb-4">
                <ClipboardCheck className="w-5 h-5 text-[#C45A3B]" />
              </div>
              <div className="font-serif text-2xl text-[#F5F2EB] mb-1">AAA Checklist</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#F5F2EB]/50 mb-3">Pre-flight · 3 beats</div>
              <div className="text-sm text-[#F5F2EB]/60 leading-relaxed">
                Agency · Autonomy · Accountability — the three-beat check-in before any conversation that actually matters.
              </div>
              <div className="mt-4 inline-flex items-center gap-1 text-[#C45A3B] text-sm font-medium">
                Enter <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </button>

            <button
              onClick={() => setModule(VOICE_MODULE.OLG)}
              className="group text-left bg-[#252018] rounded-2xl p-6 border border-[#F5F2EB]/[0.07] hover:border-[#C45A3B]/60 hover:bg-[#2C2620] transition-all"
              data-testid="voice-module-olg"
            >
              <div className="w-11 h-11 rounded-xl bg-[#C45A3B]/15 border border-[#C45A3B]/30 flex items-center justify-center mb-4">
                <Radio className="w-5 h-5 text-[#C45A3B]" />
              </div>
              <div className="font-serif text-2xl text-[#F5F2EB] mb-1">Operation Listen</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#F5F2EB]/50 mb-3">Multiplayer · 3–4 players</div>
              <div className="text-sm text-[#F5F2EB]/60 leading-relaxed">
                Real-time drill on separate phones. Coach spots the disruption, Guide lands the technique, Player B plays the chain.
              </div>
              <div className="mt-4 inline-flex items-center gap-1 text-[#C45A3B] text-sm font-medium">
                Enter <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </button>
          </div>

          {/* Voice drills */}
          <div className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-4 h-4 text-[#86AC93]" />
              <div className="text-[11px] font-medium tracking-[0.22em] uppercase text-[#F5F2EB]/60">
                Voice drills
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {VI_DRILLS.map((d) => {
                const seen = Boolean(seenMap[`vi-${d.id}`]);
                return (
                  <button
                    key={d.id}
                    onClick={() => {
                      markDrillSeen(`vi-${d.id}`);
                      setSeenMap(getSeenMap());
                      setActiveDrill(d);
                    }}
                    className="relative text-left bg-[#1F1B14] rounded-xl p-4 border border-[#F5F2EB]/[0.06] hover:border-[#86AC93]/50 hover:bg-[#2C2620] transition-all"
                    data-testid={`voice-drill-${d.id}`}
                  >
                    {seen && (
                      <span
                        className="absolute top-3 right-3 text-[9px] tracking-[0.15em] uppercase text-[#F5F2EB]/65 bg-[#F5F2EB]/[0.06] border border-[#F5F2EB]/10 rounded-full px-2 py-0.5"
                        data-testid={`voice-drill-${d.id}-seen`}
                      >
                        Seen
                      </span>
                    )}
                    <div className="font-serif text-[17px] text-[#F5F2EB] mb-1 pr-12">{d.title}</div>
                    <div className="text-[12.5px] text-[#F5F2EB]/55 leading-snug">{d.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Daily deck */}
          <div className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <Layers className="w-4 h-4 text-[#C45A3B]" />
              <div className="text-[11px] font-medium tracking-[0.22em] uppercase text-[#F5F2EB]/60">
                Daily deck
              </div>
            </div>
            <button
              onClick={() => {
                markDrillSeen(`fn-${FLASHCARDS.id}`);
                setSeenMap(getSeenMap());
                setActiveDrill(FLASHCARDS);
              }}
              className="w-full text-left bg-[#252018] rounded-2xl p-5 border border-[#F5F2EB]/[0.07] hover:border-[#C45A3B]/50 hover:bg-[#2C2620] transition-all"
              data-testid="voice-flashcards"
            >
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 shrink-0 rounded-xl bg-[#C45A3B]/15 border border-[#C45A3B]/30 flex items-center justify-center">
                  <Layers className="w-5 h-5 text-[#C45A3B]" />
                </div>
                <div className="flex-1">
                  <div className="font-serif text-xl text-[#F5F2EB] mb-0.5">{FLASHCARDS.title}</div>
                  <div className="text-[13px] text-[#F5F2EB]/60 leading-snug">{FLASHCARDS.desc}</div>
                </div>
                {seenMap[`fn-${FLASHCARDS.id}`] && (
                  <span
                    className="text-[9px] tracking-[0.15em] uppercase text-[#F5F2EB]/65 bg-[#F5F2EB]/[0.06] border border-[#F5F2EB]/10 rounded-full px-2 py-0.5"
                    data-testid="voice-flashcards-seen"
                  >
                    Seen
                  </span>
                )}
                <ArrowRight className="w-4 h-4 text-[#C45A3B]" />
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceIntegration;
